'use client';

import { Share2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';

import { ProfileShareDialog, ProfileShareDrawer } from '@ui/components/features/profile/profileShare';
import { Button } from '@ui/components/ui/button';
import { useIsMobile } from '@website/hooks/useIsMobile';
import { getProfileUrl } from '@website/utils/url';

interface ProfessionalShareButtonProps {
  professionalId: string;
  professionalName: string;
  label?: string;
  iconOnly?: boolean;
  className?: string;
}

export function ProfessionalShareButton({
  professionalId,
  professionalName,
  label,
  iconOnly = false,
  className = ''
}: ProfessionalShareButtonProps) {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const tProfile = useTranslations('Profile');
  const [isOpen, setIsOpen] = useState(false);

  const profileUrl = getProfileUrl(professionalId, locale);
  const logoUrl = '/logo-title.svg';

  const handleSaveAndDownload = (qrCodeDataUrl: string) => {
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `${professionalName.replace(/\s+/g, '-')}-profile-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const translations = {
    shareDescription: tProfile('shareProfileDescription'),
    profileUrl: tProfile('profileUrl'),
    saveAndDownload: tProfile('saveAndDownload'),
    share: tProfile('shareProfile'),
    shareVia: tProfile('shareVia'),
    copyLink: tProfile('copyLink'),
    shareViaWhatsApp: tProfile('shareViaWhatsApp'),
    shareViaFacebook: tProfile('shareViaFacebook'),
    shareViaX: tProfile('shareViaX'),
    shareViaEmail: tProfile('shareViaEmail'),
    moreOptions: tProfile('moreOptions'),
    linkCopied: tProfile('linkCopied')
  };

  const shareProps = {
    isOpen,
    onClose: () => setIsOpen(false),
    profileUrl,
    userName: professionalName,
    logoUrl,
    roleText: tProfile('roleText'),
    title: tProfile('shareProfile'),
    translations,
    onSaveAndDownload: handleSaveAndDownload
  };

  return (
    <>
      {iconOnly ? (
        <button
          type="button"
          aria-label="Share profile"
          onClick={() => setIsOpen(true)}
          className={`flex items-center justify-center size-10 rounded-full bg-white text-neutral-900 shadow-md cursor-pointer hover:bg-neutral-50 hover:shadow-lg transition-all ${className}`}
        >
          <Share2 className="size-5" strokeWidth={1.5} />
        </button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          aria-label="Share profile"
          onClick={() => setIsOpen(true)}
          className={className}
        >
          <Share2 className="size-4" strokeWidth={1.5} />
          {label && <span>{label}</span>}
        </Button>
      )}

      {!isMobile ? <ProfileShareDialog {...shareProps} /> : <ProfileShareDrawer {...shareProps} />}
    </>
  );
}
